import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Modal, Button } from 'components/common';
import { FaCopy, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

// CsdRecalculateService already returns why each record failed; the toast only had
// room for the job numbers. This lists every failure with its reason so a run can
// be diagnosed without reading server logs.
const RecalculateFailuresDialog = ({ period, summary, failures, onHide }) => {
  const [copied, setCopied] = useState(false);

  // Same reason repeated across many buildings is the common case, so group by it.
  const groups = useMemo(() => {
    const map = new Map();
    failures.forEach((item) => {
      const reason = item.message || 'Unknown error';
      if (!map.has(reason)) map.set(reason, []);
      map.get(reason).push(item);
    });
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [failures]);

  const asText = useMemo(
    () =>
      groups
        .map(([reason, items]) =>
          [
            `${reason} (${items.length})`,
            ...items.map((i) =>
              `  - ${[i.job_number, i.building_name].filter(Boolean).join(' / ') || `monthly ${i.monthly_id}`}` +
              `${i.release_month ? ` — ${i.release_month}` : ''}`
            ),
          ].join('\n')
        )
        .join('\n\n'),
    [groups]
  );

  const handleCopy = () => {
    navigator.clipboard?.writeText(`Recalculation failures — ${period}\n\n${asText}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  };

  return (
    <Modal
      show
      onHide={onHide}
      size="xxl"
      title={`Recalculation failures — ${period}`}
      footer={
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
          <Button variant="secondary" onClick={handleCopy}>
            {copied ? <FaCheck className="tw-mr-1.5" /> : <FaCopy className="tw-mr-1.5" />}
            {copied ? 'Copied' : 'Copy details'}
          </Button>
          <Button variant="primary" onClick={onHide}>Close</Button>
        </div>
      }
    >
      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">{summary}</p>

      <div className="tw-space-y-4 tw-max-h-[60vh] tw-overflow-y-auto">
        {groups.map(([reason, items]) => (
          <div key={reason} className="tw-border tw-border-amber-200 tw-rounded tw-overflow-hidden">
            <div className="tw-bg-amber-50 tw-px-3 tw-py-2 tw-flex tw-items-start tw-gap-2">
              <FaExclamationTriangle className="tw-text-amber-500 tw-mt-0.5 tw-shrink-0" />
              <div>
                <div className="tw-text-sm tw-font-semibold tw-text-amber-900">{reason}</div>
                <div className="tw-text-xs tw-text-amber-700">
                  {items.length} record{items.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <table className="tw-w-full tw-text-xs">
              <thead className="tw-bg-gray-50 tw-text-gray-600">
                <tr>
                  <th className="tw-text-left tw-px-3 tw-py-1.5 tw-font-medium">Job no.</th>
                  <th className="tw-text-left tw-px-3 tw-py-1.5 tw-font-medium">Project</th>
                  <th className="tw-text-left tw-px-3 tw-py-1.5 tw-font-medium">Building</th>
                  <th className="tw-text-left tw-px-3 tw-py-1.5 tw-font-medium">Month</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.monthly_id || `${item.job_number}-${item.building_id}-${item.month_index}`}
                      className="tw-border-t tw-border-gray-100">
                    <td className="tw-px-3 tw-py-1.5 tw-font-mono">
                      {item.project_id ? (
                        <Link
                          to={`/crm/projects/${item.project_id}`}
                          target="_blank"
                          className="tw-text-blue-600 tw-no-underline hover:tw-underline"
                        >
                          {item.job_number || '—'}
                        </Link>
                      ) : (item.job_number || '—')}
                    </td>
                    <td className="tw-px-3 tw-py-1.5">{item.project_name || '—'}</td>
                    <td className="tw-px-3 tw-py-1.5">{item.building_name || '—'}</td>
                    <td className="tw-px-3 tw-py-1.5">{item.release_month || `M+${item.month_index || '?'}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </Modal>
  );
};

RecalculateFailuresDialog.propTypes = {
  period: PropTypes.string,
  summary: PropTypes.string,
  failures: PropTypes.arrayOf(PropTypes.object).isRequired,
  onHide: PropTypes.func.isRequired,
};

export default RecalculateFailuresDialog;
